// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'

// Module Imports
import {
  assignUserRoles,
  createUserRole,
  deleteUserRole,
  editUserRole,
  getAllUserPermissions,
  getUserRoleAssignedUsers,
  getUserRoleDetail,
  getUserRoleList,
  getUserRolesContext,
  getUserRoleAutoGrants,
  saveUserRoleAutoGrants,
} from '@/modules/user-role/user-role.service'
import type {
  TAssignUserRolesRequest,
  TCreateUserRoleRequest,
  TEditUserRoleRequest,
  TUserRole,
  TUserRoleAssignedUsersQuery,
  TUserRolesContextQuery,
  TAutoGrants,
} from '@/modules/user-role/user-role.types'

export const userRoleListQueryOptions = () =>
  queryOptions({
    queryKey: ['userRoleList'],
    queryFn: getUserRoleList,
  })

export const userRoleDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['userRoleDetail', id],
    queryFn: () => getUserRoleDetail(id),
    staleTime: 0,
    enabled: Boolean(id),
  })

export const allUserPermissionsQueryOptions = () =>
  queryOptions({
    queryKey: ['allUserPermissions'],
    queryFn: getAllUserPermissions,
  })

// Backs the role-detail assign picker: paginated + searchable + role-filterable
// players plus the available roles, in one call. Replaces the old
// assignableUsers (`/admin/players`) query.
export const userRolesContextQueryOptions = (
  query: TUserRolesContextQuery = {}
) =>
  queryOptions({
    queryKey: ['userRolesContext', query],
    queryFn: () => getUserRolesContext(query),
    staleTime: 1000 * 30,
  })

export const userRoleAssignedUsersQueryOptions = (
  roleId: string,
  query: TUserRoleAssignedUsersQuery
) =>
  queryOptions({
    queryKey: ['userRoleAssignedUsers', roleId, query],
    queryFn: () => getUserRoleAssignedUsers(roleId, query),
    enabled: Boolean(roleId),
  })

export function useCreateUserRole({
  onSuccess,
  onError,
}: TMutation<TUserRole, TResponseError, TCreateUserRoleRequest>) {
  return useMutation<TUserRole, TResponseError, TCreateUserRoleRequest>({
    mutationFn: createUserRole,
    onSuccess,
    onError,
  })
}

export function useEditUserRole({
  onSuccess,
  onError,
}: TMutation<TUserRole, TResponseError, TEditUserRoleRequest>) {
  return useMutation<TUserRole, TResponseError, TEditUserRoleRequest>({
    mutationFn: editUserRole,
    onSuccess,
    onError,
  })
}

export function useDeleteUserRole({
  onSuccess,
  onError,
}: TMutation<string, TResponseError, string>) {
  return useMutation<string, TResponseError, string>({
    mutationFn: deleteUserRole,
    onSuccess,
    onError,
  })
}

export function useAssignUserRoles({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TAssignUserRolesRequest>) {
  return useMutation<void, TResponseError, TAssignUserRolesRequest>({
    mutationFn: assignUserRoles,
    onSuccess,
    onError,
  })
}

export const userRoleAutoGrantsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['userRoleAutoGrants', id],
    queryFn: () => getUserRoleAutoGrants(id),
    staleTime: 0,
    enabled: Boolean(id),
  })

export function useSaveUserRoleAutoGrants({
  onSuccess,
  onError,
}: TMutation<
  TAutoGrants,
  TResponseError,
  { id: string; payload: TAutoGrants }
>) {
  return useMutation<
    TAutoGrants,
    TResponseError,
    { id: string; payload: TAutoGrants }
  >({
    mutationFn: ({ id, payload }) => saveUserRoleAutoGrants(id, payload),
    onSuccess,
    onError,
  })
}
